import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Users, Plus, ArrowsClockwise, ShieldCheck, Pause, Play, CheckCircle, Clock,
  ArrowsLeftRight, ArrowCounterClockwise,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PilotManagementPage({ initialEnrollOrgId = null } = {}) {
  const [tab, setTab] = useState(initialEnrollOrgId ? "enroll" : "tenants");
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newPilot, setNewPilot] = useState({ name: "", primary_contact_email: "", days: 30 });
  const [agreements, setAgreements] = useState([]);

  // Enroll-existing-org tab state
  const [eligibleOrgs, setEligibleOrgs] = useState([]);
  const [enrollForm, setEnrollForm] = useState({
    org_id: initialEnrollOrgId || "",
    days: 30,
    expires_at: "",
    require_agreement: true,
    pilot_expiry_mode: "hard_block",
  });
  const [enrolling, setEnrolling] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a, e] = await Promise.all([
        axios.get(`${API}/admin/pilots`),
        axios.get(`${API}/admin/agreements`),
        axios.get(`${API}/admin/organizations/eligible-for-pilot`),
      ]);
      setPilots(p.data.pilots || []);
      setAgreements(a.data.agreements || []);
      setEligibleOrgs(e.data.organizations || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pilots");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createPilot = async () => {
    if (!newPilot.name) { toast.error("Name is required"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/pilots`, newPilot);
      toast.success(`Pilot tenant '${newPilot.name}' created`);
      setNewPilot({ name: "", primary_contact_email: "", days: 30 });
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const action = async (id, name, kind, body) => {
    try {
      await axios.post(`${API}/admin/pilots/${id}/${kind}`, body || {});
      toast.success(`${kind} → '${name}'`);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || `${kind} failed`); }
  };

  const extend = (p, days) => action(p.id, p.name, "extend", { days });

  const enrollExisting = async () => {
    if (!enrollForm.org_id) { toast.error("Pick an organization"); return; }
    setEnrolling(true);
    try {
      const body = {
        org_id:             enrollForm.org_id,
        days:               Number(enrollForm.days) || 30,
        require_agreement:  enrollForm.require_agreement,
        pilot_expiry_mode:  enrollForm.pilot_expiry_mode,
      };
      if (enrollForm.expires_at) body.expires_at = new Date(enrollForm.expires_at).toISOString();
      await axios.post(`${API}/admin/organizations/${enrollForm.org_id}/enroll-pilot`, body);
      const orgName = eligibleOrgs.find((o) => o.id === enrollForm.org_id)?.name || "org";
      toast.success(`'${orgName}' enrolled as pilot`);
      setEnrollForm({ org_id: "", days: 30, expires_at: "", require_agreement: true, pilot_expiry_mode: "hard_block" });
      setTab("tenants");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrollment failed");
    } finally { setEnrolling(false); }
  };

  return (
    <div className="p-6 space-y-5" data-testid="pilot-management-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-bold text-zinc-100 flex items-center gap-2">
            <Users size={18} weight="duotone" className="text-teal-400" />
            Pilot Management
          </h1>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Manage pilot tenants, extend evaluation periods, suspend, reactivate, or convert to production.
            All actions are audit-logged.
          </p>
        </div>
        <Button onClick={fetchAll} variant="outline" size="sm"
          className="border-zinc-700 hover:bg-zinc-800 text-xs">
          <ArrowsClockwise size={11} className="mr-1" /> Refresh
        </Button>
      </header>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-zinc-800">
        <TabBtn label="Pilot tenants"
          active={tab === "tenants"}
          onClick={() => setTab("tenants")}
          testid="pilot-tab-tenants" />
        <TabBtn label="Enroll existing org as pilot"
          active={tab === "enroll"}
          onClick={() => setTab("enroll")}
          testid="pilot-tab-enroll" />
      </div>

      {tab === "enroll" && (
        <Card className="bg-zinc-900 border-zinc-800 p-4"
          data-testid="enroll-pilot-card">
          <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3 flex items-center gap-1.5">
            <ArrowsLeftRight size={12} /> Enroll Existing Org as Pilot
          </h2>
          <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
            Flip an existing production organization into a pilot tenant. All
            users, devices, shipments, integrations, alerts, and audit
            history are preserved untouched — only the pilot classification
            fields change.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Organization</label>
              <select value={enrollForm.org_id}
                onChange={(e) => setEnrollForm((f) => ({ ...f, org_id: e.target.value }))}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-sm text-xs px-2 py-2 text-zinc-100"
                data-testid="enroll-pilot-org-select">
                <option value="">— pick an org —</option>
                {eligibleOrgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
              {eligibleOrgs.length === 0 && (
                <p className="text-[10px] text-zinc-500 mt-1">
                  No eligible production organizations. (Already-pilot tenants
                  are managed in the other tab.)
                </p>
              )}
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Days</label>
              <Input type="number" min={1} value={enrollForm.days}
                onChange={(e) => setEnrollForm((f) => ({ ...f, days: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-xs"
                data-testid="enroll-pilot-days" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Or expiration date</label>
              <Input type="date" value={enrollForm.expires_at}
                onChange={(e) => setEnrollForm((f) => ({ ...f, expires_at: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-xs"
                data-testid="enroll-pilot-expires-at" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Expiry mode</label>
              <select value={enrollForm.pilot_expiry_mode}
                onChange={(e) => setEnrollForm((f) => ({ ...f, pilot_expiry_mode: e.target.value }))}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-sm text-xs px-2 py-2 text-zinc-100"
                data-testid="enroll-pilot-expiry-mode">
                <option value="hard_block">hard_block — block login after expiry</option>
                <option value="read_only">read_only — allow login, block writes</option>
              </select>
            </div>
            <Button onClick={enrollExisting} disabled={enrolling || !enrollForm.org_id}
              className="bg-teal-500 hover:bg-teal-400 text-zinc-950 font-semibold text-xs"
              data-testid="enroll-pilot-submit">
              {enrolling ? "Enrolling…" : "Enroll as Pilot"}
            </Button>
          </div>
          <label className="flex items-center gap-2 mt-3 text-[11px] text-zinc-300 cursor-pointer">
            <input type="checkbox" checked={enrollForm.require_agreement}
              onChange={(e) => setEnrollForm((f) => ({ ...f, require_agreement: e.target.checked }))}
              className="accent-teal-500"
              data-testid="enroll-pilot-require-agreement" />
            <span>
              Require <b>Agreement Acceptance</b> on next login (recommended).
              When unchecked, the pilot starts immediately but users are not
              forced through the agreement gate until you trigger it later.
            </span>
          </label>
        </Card>
      )}

      {tab === "tenants" && (
        <>
          {/* Create pilot */}
          <Card className="bg-zinc-900 border-zinc-800 p-4">
            <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3 flex items-center gap-1.5">
              <Plus size={12} /> Create New Pilot Tenant
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <Input placeholder="Tenant name" value={newPilot.name}
                onChange={(e) => setNewPilot((p) => ({ ...p, name: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-xs"
                data-testid="pilot-create-name" />
              <Input placeholder="Primary contact email" value={newPilot.primary_contact_email}
                onChange={(e) => setNewPilot((p) => ({ ...p, primary_contact_email: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-xs" />
              <Input type="number" placeholder="Days (default 30)" value={newPilot.days}
                onChange={(e) => setNewPilot((p) => ({ ...p, days: Number(e.target.value) || 30 }))}
                className="bg-zinc-950 border-zinc-800 text-xs" />
              <Button onClick={createPilot} disabled={creating}
                className="bg-teal-500 hover:bg-teal-400 text-zinc-950 font-semibold text-xs"
                data-testid="pilot-create-submit">
                {creating ? "Creating…" : "Create Pilot"}
              </Button>
            </div>
          </Card>

      {/* Pilot table */}
      <Card className="bg-zinc-900 border-zinc-800 p-0 overflow-hidden">
        {loading ? (
          <div className="p-6 text-xs font-mono text-teal-400">Loading…</div>
        ) : pilots.length === 0 ? (
          <div className="p-6 text-xs text-zinc-500">No pilot tenants yet.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left">Tenant</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Started</th>
                <th className="px-3 py-2 text-left">Expires</th>
                <th className="px-3 py-2 text-right">Days left</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pilots.map((p) => (
                <tr key={p.id} className="border-t border-zinc-800 hover:bg-zinc-900/50"
                  data-testid={`pilot-row-${p.id}`}>
                  <td className="px-3 py-2 font-medium text-zinc-100">
                    {p.name}
                    {p.primary_contact_email && <div className="text-[10px] text-zinc-500 font-mono">{p.primary_contact_email}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={p.pilot_status} isExpired={p.is_expired} />
                    {p.agreement_required && (
                      <div className="text-[9px] font-bold uppercase tracking-wider mt-1 inline-block bg-amber-500/15 text-amber-300 px-1.5 py-0.5 rounded"
                        title={`Acceptance of v${p.required_agreement_version || "?"} required since ${(p.agreement_required_at||"").slice(0,10)} by ${p.agreement_required_by||"?"}`}
                        data-testid={`pilot-agreement-required-badge-${p.id}`}>
                        agreement v{p.required_agreement_version || "?"} required
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-400">
                    {(p.pilot_started_at || "—").slice(0, 10)}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-400">
                    {(p.pilot_expires_at || "—").slice(0, 10)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    <span className={
                      p.is_expired ? "text-red-300" :
                      (p.days_remaining ?? 99) <= 3 ? "text-red-300" :
                      (p.days_remaining ?? 99) <= 14 ? "text-amber-300" : "text-emerald-300"
                    }>
                      {p.is_expired ? "expired" : (p.days_remaining ?? "—")}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1 flex-wrap">
                      <ActionBtn icon={<Clock size={10} />} onClick={() => extend(p, 30)} label="+30d" testid={`pilot-extend-30-${p.id}`} />
                      <ActionBtn icon={<Clock size={10} />} onClick={() => extend(p, 60)} label="+60d" />
                      <ActionBtn icon={<Clock size={10} />} onClick={() => extend(p, 90)} label="+90d" />
                      {p.pilot_status === "suspended"
                        ? <ActionBtn icon={<Play size={10} />} onClick={() => action(p.id, p.name, "reactivate")} label="Reactivate" tone="emerald" />
                        : <ActionBtn icon={<Pause size={10} />} onClick={() => action(p.id, p.name, "suspend")} label="Suspend" tone="amber" />}
                      {p.agreement_required ? (
                        <>
                          <ActionBtn icon={<ShieldCheck size={10} />}
                            onClick={() => {
                              if (window.confirm(`Force RE-acceptance of agreement for '${p.name}'? All existing acceptances for the current version will be wiped and every user will see the gate on next login.`)) {
                                action(p.id, p.name, "require-reacceptance");
                              }
                            }}
                            label="Re-accept"
                            tone="amber"
                            testid={`pilot-require-reaccept-${p.id}`}
                          />
                          <ActionBtn icon={<ShieldCheck size={10} />}
                            onClick={() => {
                              if (window.confirm(`Clear the agreement requirement for '${p.name}'? Users will be able to access the platform without accepting the agreement.`)) {
                                action(p.id, p.name, "clear-agreement-requirement");
                              }
                            }}
                            label="Clear req."
                            tone="zinc"
                            testid={`pilot-clear-agreement-${p.id}`}
                          />
                        </>
                      ) : (
                        <ActionBtn icon={<ShieldCheck size={10} />}
                          onClick={() => {
                            if (window.confirm(`Require '${p.name}' users to accept the current agreement before next login?`)) {
                              action(p.id, p.name, "require-agreement");
                            }
                          }}
                          label="Require agreement"
                          tone="teal"
                          testid={`pilot-require-agreement-${p.id}`}
                        />
                      )}
                      <ActionBtn icon={<CheckCircle size={10} />} onClick={() => {
                        if (window.confirm(`Convert '${p.name}' to production? No data will be migrated or deleted.`)) {
                          action(p.id, p.name, "convert");
                        }
                      }} label="Convert" tone="teal" testid={`pilot-convert-${p.id}`} />
                      <ActionBtn icon={<ArrowCounterClockwise size={10} />} onClick={() => {
                        if (window.confirm(`Revert '${p.name}' to production?\n\n• tenant_type → production\n• pilot_status → reverted\n• Agreement gate cleared\n• ALL data preserved (devices, shipments, audit, acceptances)\n• Pilot history retained for audit\n\nProceed?`)) {
                          action(p.id, p.name, "revert-to-production");
                        }
                      }} label="Revert" tone="zinc" testid={`pilot-revert-${p.id}`} />
                      <ActionBtn icon={<ArrowsLeftRight size={10} />} onClick={() => {
                        const cur = (p.pilot_expiry_mode || "hard_block");
                        const next = cur === "hard_block" ? "read_only" : "hard_block";
                        if (window.confirm(`Change expiry mode for '${p.name}' from '${cur}' to '${next}'?`)) {
                          action(p.id, p.name, "expiry-mode", { mode: next });
                        }
                      }} label={`mode: ${(p.pilot_expiry_mode || "hard_block")}`} tone="zinc"
                        testid={`pilot-expiry-mode-${p.id}`} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Agreement versions */}
      <Card className="bg-zinc-900 border-zinc-800 p-4">
        <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2 flex items-center gap-1.5">
          <ShieldCheck size={12} className="text-teal-400" /> Agreement Versions
        </h2>
        {agreements.length === 0 ? (
          <div className="text-xs text-zinc-500">No agreements yet.</div>
        ) : (
          <div className="space-y-1.5">
            {agreements.map((a) => (
              <div key={a.id} className="flex items-center justify-between px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-100">{a.title}</span>
                  <span className="text-[10px] font-mono text-zinc-500">v{a.version}</span>
                  {a.is_active && <span className="text-[9px] font-bold uppercase bg-emerald-500/15 text-emerald-300 px-1.5 py-0.5 rounded">Active</span>}
                </div>
                <a href={`${API}/agreements/${a.id}/pdf`} target="_blank" rel="noopener noreferrer"
                  className="text-[10px] text-teal-300 hover:text-teal-200 underline">Download PDF</a>
              </div>
            ))}
          </div>
        )}
        <p className="text-[10px] text-zinc-500 italic mt-2">
          New version publishing UI ships in Phase B. For now use <span className="font-mono">POST /api/admin/agreements</span>.
        </p>
      </Card>
        </>
      )}
    </div>
  );
}

function TabBtn({ label, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-2 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
        active
          ? "text-teal-300 border-b-2 border-teal-400"
          : "text-zinc-500 hover:text-zinc-300 border-b-2 border-transparent"
      }`}>
      {label}
    </button>
  );
}

function StatusBadge({ status, isExpired }) {
  const s = (status || "active").toLowerCase();
  const effective = isExpired ? "expired" : s;
  const tone = {
    active:     "bg-emerald-500/15 text-emerald-300",
    suspended:  "bg-amber-500/15 text-amber-300",
    expired:    "bg-red-500/15 text-red-300",
    converted:  "bg-sky-500/15 text-sky-300",
    reverted:   "bg-zinc-700/40 text-zinc-300",
  }[effective] || "bg-zinc-800 text-zinc-300";
  return (
    <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${tone}`}>{effective}</span>
  );
}

function ActionBtn({ icon, label, onClick, tone = "zinc", testid }) {
  const cls = {
    zinc:    "border-zinc-700 text-zinc-300 hover:bg-zinc-800",
    teal:    "border-teal-700/60 text-teal-300 hover:bg-teal-500/10",
    amber:   "border-amber-700/60 text-amber-300 hover:bg-amber-500/10",
    emerald: "border-emerald-700/60 text-emerald-300 hover:bg-emerald-500/10",
  }[tone];
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono border rounded ${cls}`}>
      {icon} {label}
    </button>
  );
}
